import { ConsultationPreview } from "components/ConsultationPreview/ConsultationPreview";
import { SearchInput } from "components/ui";
import { Button } from "components/ui/Button";
import { DataTablePagination } from "components/ui/DataTable/components/DataTablePagination/DataTablePagination";
import { useConsultationViewState } from "features/pregnancy/hooks/useConsultationViewState";
import { usePaginatedConsultations } from "features/pregnancy/hooks/usePaginatedConsultations";
import { usePostnatalConsultations } from "features/pregnancy/hooks/usePostnatalConsultations";

import { PregnancyLoader } from "../../PregnancyLoader/PregnancyLoader";
import { PregnancyNoPostNatalConsultationsFound } from "../PregnancyNoPostNatalConsultationsFound/PregnancyNoPostNatalConsultationsFound";
import { PregnancyPostNatalConsultationContent } from "../PregnancyPostNatalConsultation/PregnancyPostNatalConsultationContent";
import { PregnancyPostNatalConsultationForm } from "../PregnancyPostNatalConsultationForm/PregnancyPostNatalConsultationForm";
import style from "./PregnancyPostNatalView.module.css";

export const PregnancyPostNatalView = ({ pregnancy, tenantVal, patientId }) => {
  const {
    activeConsultation,
    isFormOpen,
    page,
    setPage,
    rowsPerPage,
    search,
    openAdd,
    openEdit,
    closeForm,
    handleSearchChange,
    handleRowsChange,
  } = useConsultationViewState();

  const { isLoading, data: postNatalConsultations } = usePostnatalConsultations(pregnancy.id);

  const { paginated, total, totalPages, start, end } = usePaginatedConsultations({
    consultations: postNatalConsultations,
    searchTerm: search,
    searchFields: [(c) => c.title, (c) => c.date, (c) => c.subject?.issuesOfNote],
    page,
    rowsPerPage,
  });

  if (isLoading) {
    return (
      <PregnancyLoader>Loading post-natal consultations...</PregnancyLoader>
    );
  }

  if (isFormOpen) {
    return (
      <div className={style.pregnancyPostNatalView}>
        <PregnancyPostNatalConsultationForm
          closeView={closeForm}
          tenantVal={tenantVal}
          pregnancyId={pregnancy.id}
          patientId={patientId}
          postNatalConsultation={activeConsultation}
          pregnancy={pregnancy}
        />
      </div>
    );
  }

  if (postNatalConsultations?.length < 1) {
    return (
      <PregnancyNoPostNatalConsultationsFound
        onStartConsultationButtonClick={openAdd}
      />
    );
  }

  return (
    <div className={style.pregnancyPostNatalView}>
      <div className={style.viewHeader}>
        <h1 className={style.pregnancyPostNatalViewTitle}>
          Post-natal consultations
        </h1>
        <Button onClick={openAdd}>Start consultation</Button>
      </div>
      <div className={style.table}>
        <div className={style.tableTopBarWrapper}>
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Search consultations"
          />
        </div>
        <div className={style.consultationPreviewWrapper}>
          {paginated.map((consultation) => {
            const s = consultation.subject ?? {};
            const o = consultation.objective ?? {};
            const m = consultation.mother ?? {};
            const parts = [];
            if (s.weightAtBirth) parts.push(`Birth weight: ${s.weightAtBirth} g`);
            if (o.weight) parts.push(`Weight: ${o.weight} g`);
            if (s.feeding) parts.push(`Feeding: ${s.feeding}`);
            if (o.length) parts.push(`Length: ${o.length} cm`);
            if (o.headCircumference) parts.push(`HC: ${o.headCircumference} cm`);
            if (o.impression) parts.push(o.impression);
            if (m.postNatalMood) parts.push(`Mood: ${m.postNatalMood}`);
            if (m.contraception) parts.push(`Contraception: ${m.contraception}`);
            if (s.issuesOfNote) parts.push(`Issues: ${s.issuesOfNote}`);
            const previewText =
              parts.length > 0 ? parts.join("  ·  ") : "Post-natal consultation";

            return (
              <ConsultationPreview
                key={consultation.id}
                consultation={consultation}
                previewText={previewText}
                onEditConsultation={() => openEdit(consultation)}
              >
                <PregnancyPostNatalConsultationContent
                  consultation={consultation}
                />
              </ConsultationPreview>
            );
          })}
        </div>
        <DataTablePagination
          start={start}
          end={end}
          total={total}
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
          rowsOptions={[10, 20, 50]}
          rowsPerPage={rowsPerPage}
          onRowsChange={handleRowsChange}
        />
      </div>
    </div>
  );
};
